import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Plus, Search, X, AlertTriangle, Clock, CheckCircle, AlertCircle, Edit2, RefreshCw, Eye, Trash2, Banknote, Wrench, Monitor, Stethoscope, FlaskConical, Building2, Armchair, Car, Zap, Wind, Scissors, MoreHorizontal, ChevronUp, ChevronDown, Calendar, MapPin, User as UserIcon, Briefcase, Tag, Hash, Truck, Shield } from 'lucide-react';
import { assetAPI, vendorAPI } from '../services/api';
import { useToast } from './UIProvider';
import DatePicker from './DatePicker';
import { toTitleCase } from '../utils/text';
import Select from './Select';
import TablePager, { usePagedRows } from './TablePager';
import { formatClinicDate, clinicToday, toClinicDate } from '../utils/clinicTime';
import { PAYMENT_MODES, PAYMENT_MODE_LABEL } from './StockReceiptsManagement';
import { paymentNeedsProof } from '../constants/payments';
import PaymentProofUpload from './PaymentProofUpload';
import useDialog from '../hooks/useDialog';
import { onActivateKey, ROW_FOCUS_RING } from '../utils/keyboard';

// Alphabetical by label; OTHER pinned to the end.
const CATEGORY_CONFIG = {
  ELECTRICAL: { label: 'Electrical', icon: Zap, color: 'from-yellow-500 to-amber-600' },
  FURNITURE: { label: 'Furniture', icon: Armchair, color: 'from-amber-500 to-orange-600' },
  HVAC: { label: 'HVAC', icon: Wind, color: 'from-sky-500 to-blue-600' },
  IMAGING_EQUIPMENT: { label: 'Imaging Equipment', icon: Monitor, color: 'from-blue-500 to-indigo-600' },
  IT_EQUIPMENT: { label: 'IT Equipment', icon: Monitor, color: 'from-cyan-500 to-blue-600' },
  LABORATORY_EQUIPMENT: { label: 'Laboratory Equipment', icon: FlaskConical, color: 'from-purple-500 to-violet-600' },
  MEDICAL_EQUIPMENT: { label: 'Medical Equipment', icon: Stethoscope, color: 'from-emerald-500 to-teal-600' },
  PATIENT_CARE: { label: 'Patient Care', icon: Building2, color: 'from-pink-500 to-rose-600' },
  SURGICAL_INSTRUMENTS: { label: 'Surgical Instruments', icon: Scissors, color: 'from-red-500 to-rose-600' },
  VEHICLE: { label: 'Vehicle', icon: Car, color: 'from-gray-500 to-slate-600' },
  OTHER: { label: 'Other', icon: Box, color: 'from-slate-500 to-gray-600' }
};

const STATUS_CONFIG = {
  ACTIVE: { label: 'Active', color: 'bg-emerald-100 text-emerald-800' },
  INACTIVE: { label: 'Inactive', color: 'bg-gray-100 text-gray-600' },
  UNDER_MAINTENANCE: { label: 'Under Maintenance', color: 'bg-amber-100 text-amber-800' },
  DISPOSED: { label: 'Disposed', color: 'bg-red-100 text-red-700' },
  LOST: { label: 'Lost', color: 'bg-purple-100 text-purple-700' }
};

const CONDITION_CONFIG = {
  EXCELLENT: { label: 'Excellent', color: 'bg-emerald-100 text-emerald-800' },
  GOOD: { label: 'Good', color: 'bg-green-100 text-green-800' },
  FAIR: { label: 'Fair', color: 'bg-yellow-100 text-yellow-800' },
  POOR: { label: 'Poor', color: 'bg-orange-100 text-orange-800' },
  BROKEN: { label: 'Broken', color: 'bg-red-100 text-red-800' }
};

const CATEGORY_BADGE_COLORS = {
  MEDICAL_EQUIPMENT: 'bg-emerald-100 text-emerald-800',
  SURGICAL_INSTRUMENTS: 'bg-red-100 text-red-800',
  LABORATORY_EQUIPMENT: 'bg-purple-100 text-purple-800',
  IMAGING_EQUIPMENT: 'bg-blue-100 text-blue-800',
  PATIENT_CARE: 'bg-pink-100 text-pink-800',
  FURNITURE: 'bg-amber-100 text-amber-800',
  IT_EQUIPMENT: 'bg-cyan-100 text-cyan-800',
  HVAC: 'bg-sky-100 text-sky-800',
  ELECTRICAL: 'bg-yellow-100 text-yellow-800',
  VEHICLE: 'bg-gray-100 text-gray-800',
  OTHER: 'bg-slate-100 text-slate-800'
};

const AssetsManagement = ({ hasPermission }) => {
  const showToast = useToast();
  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('PHARMACY_ASSETS', action) : true),
    [hasPermission],
  );
  const [assets, setAssets] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [specialFilter, setSpecialFilter] = useState(null); // 'DUE_MAINTENANCE' | 'WARRANTY_EXPIRING' | null
  const [modal, setModal] = useState({ type: null, asset: null });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  // Close action menu on outside click, or on Escape, which hands focus back to the menu's button.
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      menuRef.current?.querySelector('button')?.focus();
      setOpenMenuId(null);
    };
    if (openMenuId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape, true);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape, true);
      };
    }
  }, [openMenuId]);

  const loadData = async () => {
    setLoading(true); setError(null);
    try {
      // Stats are derived on the client from `assets` (see useMemo below) — guarantees stats and
      // filters share the same source and can never drift.
      const [assetsData, vendorsData] = await Promise.all([assetAPI.getAll(), vendorAPI.getAll()]);
      setAssets(assetsData || []); setVendors(vendorsData || []);
    } catch { setError('Failed to load data.'); }
    finally { setLoading(false); }
  };

  // Derive stats from the same predicates the specialFilter uses.
  const stats = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let active = 0, underMaintenance = 0, maintenanceDue = 0, warrantyExpiringSoon = 0;
    let totalValue = 0;
    for (const a of assets) {
      if (a.status === 'ACTIVE') active++;
      if (a.status === 'UNDER_MAINTENANCE') underMaintenance++;
      if (a.nextMaintenanceDate) {
        // toClinicDate returns null on unparseable input, so check before using it.
        const next = toClinicDate(a.nextMaintenanceDate); // date-only; see toClinicDate
        if (next) {
          next.setHours(0, 0, 0, 0);
          if (next <= today) maintenanceDue++;
        }
      }
      if (a.warrantyExpiryDate) {
        const wexp = toClinicDate(a.warrantyExpiryDate); // date-only; see toClinicDate
        if (wexp) {
          const days = (wexp - today) / (1000 * 60 * 60 * 24);
          if (days >= 0 && days <= 30) warrantyExpiringSoon++;
        }
      }
      totalValue += Number(a.purchasePrice || 0);
    }
    return { total: assets.length, active, underMaintenance, maintenanceDue, warrantyExpiringSoon, totalValue };
  }, [assets]);

  const filteredAssets = assets.filter(a => {
    const matchesSearch = !searchTerm || [a.name, a.assetCode, a.brand].some(f => f?.toLowerCase().includes(searchTerm.toLowerCase()));
    if (!matchesSearch) return false;
    if (statusFilter !== 'ALL' && a.status !== statusFilter) return false;
    if (categoryFilter !== 'ALL' && a.category !== categoryFilter) return false;

    // Special computed filters — driven by the stat cards
    if (specialFilter === 'DUE_MAINTENANCE') {
      const due = a.nextMaintenanceDate && new Date(a.nextMaintenanceDate) <= new Date();
      if (!due) return false;
    }
    if (specialFilter === 'WARRANTY_EXPIRING') {
      if (!a.warrantyExpiryDate) return false;
      const days = (new Date(a.warrantyExpiryDate) - new Date()) / (1000 * 60 * 60 * 24);
      if (days < 0 || days > 30) return false;
    }
    return true;
  });

  const sortedAssets = React.useMemo(() => {
    if (!sortConfig.key) return filteredAssets;
    const sorted = [...filteredAssets].sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'category':
          aVal = (CATEGORY_CONFIG[a.category]?.label || '').toLowerCase();
          bVal = (CATEGORY_CONFIG[b.category]?.label || '').toLowerCase();
          break;
        case 'status':
          aVal = (STATUS_CONFIG[a.status]?.label || '').toLowerCase();
          bVal = (STATUS_CONFIG[b.status]?.label || '').toLowerCase();
          break;
        case 'condition':
          aVal = (CONDITION_CONFIG[a.conditionStatus]?.label || '').toLowerCase();
          bVal = (CONDITION_CONFIG[b.conditionStatus]?.label || '').toLowerCase();
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [filteredAssets, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="h-3.5 w-3.5 text-gray-300" />;
    }
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="h-3.5 w-3.5 text-emerald-600" />
      : <ChevronDown className="h-3.5 w-3.5 text-emerald-600" />;
  };

  const ariaSort = (columnKey) => (
    sortConfig.key !== columnKey ? 'none' : sortConfig.direction === 'asc' ? 'ascending' : 'descending'
  );

  const handleAction = async (action, data) => {
    try {
      if (action === 'create') { await assetAPI.create(data.asset, data.vendorId, data.proofs || []); showToast('success', 'Asset added'); }
      else if (action === 'update') { await assetAPI.update(modal.asset.id, data.asset, data.vendorId); showToast('success', 'Asset updated'); }
      else if (action === 'sendMaintenance') { await assetAPI.sendForMaintenance(modal.asset.id, data); showToast('success', 'Asset sent for maintenance'); }
      else if (action === 'completeMaintenance') { await assetAPI.recordMaintenance(modal.asset.id, data); showToast('success', 'Maintenance recorded'); }
      else if (action === 'dispose') { await assetAPI.dispose(modal.asset.id, data); showToast('success', 'Asset disposed'); }
      setModal({ type: null, asset: null }); loadData();
    } catch (err) { showToast('error', 'Failed: ' + err.message); }
  };

  const formatCurrency = (amt) => new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 0 }).format(amt || 0);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('ALL');
    setCategoryFilter('ALL');
    setSpecialFilter(null);
    setSortConfig({ key: null, direction: 'asc' });
  };

  const hasActiveFilters = searchTerm || statusFilter !== 'ALL' || categoryFilter !== 'ALL' || !!specialFilter;
  const paging = usePagedRows(sortedAssets, {
    id: 'assets',
    resetOn: [sortConfig, searchTerm, statusFilter, categoryFilter, specialFilter],
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-5">
                <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center shadow-lg">
                  <Box className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Assets & Equipment</h1>
                  <p className="text-gray-500 mt-0.5">Manage clinic assets</p>
                </div>
              </div>
              <button
                onClick={() => setModal({ type: 'add', asset: null })}
                className="flex items-center space-x-2 px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
              >
                <Plus className="h-5 w-5" />
                <span>Add Asset</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats — clickable quick filters. Total = clear all. */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { icon: Box,           label: 'Total',       value: stats.total,                gradient: 'from-emerald-500 to-teal-600',  kind: 'ALL' },
            { icon: CheckCircle,   label: 'Active',      value: stats.active,               gradient: 'from-green-500 to-emerald-600', kind: 'STATUS', filterValue: 'ACTIVE' },
            { icon: Wrench,        label: 'Maintenance', value: stats.underMaintenance,     gradient: 'from-amber-500 to-orange-600',  kind: 'STATUS', filterValue: 'UNDER_MAINTENANCE' },
            { icon: AlertTriangle, label: 'Due',         value: stats.maintenanceDue,       gradient: 'from-red-500 to-rose-600',      kind: 'SPECIAL', filterValue: 'DUE_MAINTENANCE' },
            { icon: Clock,         label: 'Warranty',    value: stats.warrantyExpiringSoon, gradient: 'from-purple-500 to-violet-600', kind: 'SPECIAL', filterValue: 'WARRANTY_EXPIRING' },
          ].map((s, i) => {
            const isActive =
              (s.kind === 'STATUS' && statusFilter === s.filterValue) ||
              (s.kind === 'SPECIAL' && specialFilter === s.filterValue);
            const isTotalActive = s.kind === 'ALL' && statusFilter === 'ALL' && !specialFilter && categoryFilter === 'ALL' && !searchTerm;
            const toggle = () => {
              if (s.kind === 'ALL') {
                clearFilters();
              } else if (s.kind === 'STATUS') {
                setSpecialFilter(null);
                setStatusFilter(isActive ? 'ALL' : s.filterValue);
              } else if (s.kind === 'SPECIAL') {
                setStatusFilter('ALL');
                setSpecialFilter(isActive ? null : s.filterValue);
              }
            };
            return (
              <button
                key={i}
                type="button"
                onClick={toggle}
                title={s.kind === 'ALL' ? 'Clear filters' : isActive ? 'Clear filter' : `Filter by ${s.label}`}
                className={`bg-white rounded-xl shadow-md p-4 hover:shadow-lg transition-all text-left ${
                  isActive ? 'ring-2 ring-emerald-500 shadow-lg' : ''
                } ${isTotalActive ? 'ring-2 ring-blue-400' : ''}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{s.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{s.value || 0}</p>
                  </div>
                  <div className={`w-11 h-11 bg-gradient-to-br ${s.gradient} rounded-xl flex items-center justify-center`}>
                    <s.icon className="h-5 w-5 text-white" />
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Total Value */}
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 rounded-2xl p-6 mb-8 shadow-xl flex items-center space-x-4">
          <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
            <Banknote className="h-7 w-7 text-white" />
          </div>
          <div>
            <p className="text-white/80 text-sm">Total Asset Value</p>
            <p className="text-3xl font-bold text-white">{formatCurrency(stats.totalValue)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-xl p-5 mb-6 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, code, or brand..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="w-full pl-12 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: 'ALL', label: 'All Status' }, ...Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
            className="min-w-[10rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <Select
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={[{ value: 'ALL', label: 'All Categories' }, ...Object.entries(CATEGORY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))]}
            className="min-w-[12rem]"
            buttonClassName="px-4 py-3 rounded-xl"
          />
          <button
            title="Refresh"
            onClick={loadData}
            className="p-3 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-2xl mb-6 flex items-center">
            <AlertCircle className="h-5 w-5 mr-3 flex-shrink-0" />{error}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div>
          </div>
        ) : sortedAssets.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Box className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">No assets found</h3>
            <p className="text-gray-500 mt-1 mb-4">
              {hasActiveFilters ? 'Try adjusting your filters.' : 'Get started by adding your first asset.'}
            </p>
            {hasActiveFilters ? (
              <button onClick={clearFilters} className="px-6 py-3 text-emerald-600 hover:text-emerald-700 font-medium">
                Clear filters
              </button>
            ) : (
              <button
                onClick={() => setModal({ type: 'add' })}
                className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-medium"
              >
                Add Asset
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-xl">
              <TablePager paging={paging} noun="asset" filteredFrom={assets.length} />
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th
                      className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none"
                      aria-sort={ariaSort('name')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('name')}
                        className="flex items-center space-x-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        <span>Asset</span>
                        <SortIcon columnKey="name" />
                      </button>
                    </th>
                    <th
                      className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none"
                      aria-sort={ariaSort('category')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('category')}
                        className="flex items-center space-x-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        <span>Category</span>
                        <SortIcon columnKey="category" />
                      </button>
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Brand / Model
                    </th>
                    <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th
                      className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none"
                      aria-sort={ariaSort('condition')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('condition')}
                        className="flex items-center space-x-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        <span>Condition</span>
                        <SortIcon columnKey="condition" />
                      </button>
                    </th>
                    <th
                      className="text-left px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none"
                      aria-sort={ariaSort('status')}
                    >
                      <button
                        type="button"
                        onClick={() => handleSort('status')}
                        className="flex items-center space-x-1 w-full uppercase tracking-wider rounded hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                      >
                        <span>Status</span>
                        <SortIcon columnKey="status" />
                      </button>
                    </th>
                    <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paging.pageRows.map(asset => {
                    const cc = CATEGORY_CONFIG[asset.category] || CATEGORY_CONFIG.OTHER;
                    const sc = STATUS_CONFIG[asset.status] || STATUS_CONFIG.ACTIVE;
                    const cond = CONDITION_CONFIG[asset.conditionStatus] || CONDITION_CONFIG.GOOD;
                    const catBadge = CATEGORY_BADGE_COLORS[asset.category] || CATEGORY_BADGE_COLORS.OTHER;
                    const isDue = asset.nextMaintenanceDate && new Date(asset.nextMaintenanceDate) <= new Date();

                    return (
                      <tr
                        key={asset.id}
                        tabIndex={0}
                        onClick={() => setModal({ type: 'view', asset })}
                        onKeyDown={onActivateKey(() => setModal({ type: 'view', asset }))}
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${ROW_FOCUS_RING}`}
                      >
                        {/* Name + Code */}
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div>
                              <p className="font-medium text-gray-900">{asset.name}</p>
                              <p className="text-xs text-gray-500 font-mono">{asset.assetCode}</p>
                            </div>
                            {isDue && (
                              <span title="Maintenance Due">
                                <AlertTriangle className="h-4 w-4 text-amber-500" />
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Category Badge */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${catBadge}`}>
                            {cc.label}
                          </span>
                        </td>
                        {/* Brand / Model */}
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {asset.brand || asset.model ? (
                            <div>
                              {asset.brand && <span>{asset.brand}</span>}
                              {asset.brand && asset.model && <span className="text-gray-400"> / </span>}
                              {asset.model && <span className="text-gray-500">{asset.model}</span>}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        {/* Location */}
                        <td className="px-6 py-4 text-sm text-gray-700">
                          {asset.location || <span className="text-gray-400">-</span>}
                        </td>
                        {/* Condition Badge */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cond.color}`}>
                            {cond.label}
                          </span>
                        </td>
                        {/* Status Badge */}
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                            {sc.label}
                          </span>
                        </td>
                        {/* Actions Menu */}
                        <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="relative inline-block" ref={openMenuId === asset.id ? menuRef : null} onClickCapture={(e) => { const btn = e.currentTarget.querySelector('button'); if (btn && !btn.contains(e.target)) btn.focus(); }}>
                            <button
                              onClick={() => setOpenMenuId(openMenuId === asset.id ? null : asset.id)}
                              title="More actions"
                              aria-haspopup="menu"
                              aria-expanded={openMenuId === asset.id}
                              className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              <MoreHorizontal className="h-5 w-5 text-gray-500" />
                            </button>
                            {openMenuId === asset.id && (
                              <div className="absolute right-0 mt-1 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-20">
                                <button
                                  onClick={() => { setOpenMenuId(null); setModal({ type: 'view', asset }); }}
                                  className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                >
                                  <Eye className="h-4 w-4 text-gray-400" />
                                  <span>View</span>
                                </button>
                                {can('EDIT') && (
                                  <button
                                    onClick={() => { setOpenMenuId(null); setModal({ type: 'edit', asset }); }}
                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <Edit2 className="h-4 w-4 text-blue-500" />
                                    <span>Edit</span>
                                  </button>
                                )}
                                {can('SCHEDULE_MAINTENANCE') && (
                                  <button
                                    onClick={() => { setOpenMenuId(null); setModal({ type: 'maintenance', asset }); }}
                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <Wrench className="h-4 w-4 text-amber-500" />
                                    <span>Maintenance</span>
                                  </button>
                                )}
                                {asset.status !== 'DISPOSED' && can('RETIRE_ASSET') && (
                                  <button
                                    onClick={() => { setOpenMenuId(null); setModal({ type: 'dispose', asset }); }}
                                    className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4 text-red-500" />
                                    <span>Dispose</span>
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

            {hasActiveFilters && (
              <div className="px-6 py-2 border-t border-gray-100 text-sm text-right">
                <button onClick={clearFilters} className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline transition-colors">
                  Clear filters
                </button>
              </div>
            )}
            <TablePager paging={paging} noun="asset" filteredFrom={assets.length} position="bottom" />
          </div>
        )}
      </div>

      {modal.type === 'add' && <AssetFormModal vendors={vendors} onClose={() => setModal({ type: null })} onSubmit={(d, v, proofs) => handleAction('create', { asset: d, vendorId: v, proofs })} />}
      {modal.type === 'edit' && <AssetFormModal asset={modal.asset} vendors={vendors} onClose={() => setModal({ type: null })} onSubmit={(d, v) => handleAction('update', { asset: d, vendorId: v })} />}
      {modal.type === 'view' && <ViewModal asset={modal.asset} onClose={() => setModal({ type: null })} />}
      {modal.type === 'maintenance' && <MaintenanceModal asset={modal.asset} onClose={() => setModal({ type: null })} onSend={r => handleAction('sendMaintenance', r)} onComplete={n => handleAction('completeMaintenance', n)} />}
      {modal.type === 'dispose' && <DisposeModal asset={modal.asset} onClose={() => setModal({ type: null })} onSubmit={r => handleAction('dispose', r)} />}
    </div>
  );
};

const AssetFormModal = ({ asset, vendors, onClose, onSubmit }) => {
  const isEdit = !!asset;

  // Initialize form with proper defaults - ALWAYS include status
  const [form, setForm] = useState({
    name: asset?.name || '',
    category: asset?.category || 'MEDICAL_EQUIPMENT',
    brand: asset?.brand || '',
    model: asset?.model || '',
    serialNumber: asset?.serialNumber || '',
    purchaseDate: asset?.purchaseDate || '',
    purchasePrice: asset?.purchasePrice?.toString() || '',
    warrantyExpiryDate: asset?.warrantyExpiryDate || '',
    location: asset?.location || '',
    department: asset?.department || '',
    assignedTo: asset?.assignedTo || '',
    conditionStatus: asset?.conditionStatus || 'GOOD',
    status: asset?.status || 'ACTIVE',  // Always default to ACTIVE for new assets
    maintenanceIntervalDays: asset?.maintenanceIntervalDays?.toString() || '',
    notes: asset?.notes || '',
    // New assets default to Cash; editing a legacy asset (no mode recorded) starts blank so
    // saving doesn't silently stamp a guessed mode.
    paymentMode: asset ? (asset.paymentMode || '') : 'CASH',
    paymentReference: asset?.paymentReference || ''
  });
  // The transfer screenshot for an asset bought by wallet or bank transfer.
  const [proofFiles, setProofFiles] = useState([]);
  const [proofError, setProofError] = useState(null);

  const [vendorId, setVendorId] = useState(asset?.vendor?.id || '');
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'Asset name is required';
    if (!form.category) newErrors.category = 'Category is required';
    if (!form.status) newErrors.status = 'Status is required';
    if (!form.conditionStatus) newErrors.conditionStatus = 'Condition is required';
    if (form.purchasePrice && isNaN(parseFloat(form.purchasePrice))) newErrors.purchasePrice = 'Invalid price';
    if (form.maintenanceIntervalDays && isNaN(parseInt(form.maintenanceIntervalDays))) newErrors.maintenanceIntervalDays = 'Invalid number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    // A payment that settles outside the system cannot be saved without its screenshot.
    if (paymentNeedsProof(form.paymentMode) && proofFiles.length === 0) {
      setProofError('Attach the payment screenshot for this method.');
      return;
    }

    // Build the data object with proper types
    const data = {
      name: form.name.trim(),
      category: form.category,
      status: form.status,  // Always include status
      conditionStatus: form.conditionStatus,
      brand: form.brand.trim() || null,
      model: form.model.trim() || null,
      serialNumber: form.serialNumber.trim() || null,
      purchaseDate: form.purchaseDate || null,
      purchasePrice: form.purchasePrice ? parseFloat(form.purchasePrice) : null,
      warrantyExpiryDate: form.warrantyExpiryDate || null,
      location: form.location.trim() || null,
      department: form.department.trim() || null,
      assignedTo: form.assignedTo.trim() || null,
      maintenanceIntervalDays: form.maintenanceIntervalDays ? parseInt(form.maintenanceIntervalDays) : null,
      notes: form.notes.trim() || null,
      paymentMode: form.paymentMode || null,
      paymentReference: form.paymentReference.trim() || null
    };

    onSubmit(data, vendorId || null, proofFiles);
  };

  const activeVendors = vendors.filter(v => v.status === 'ACTIVE');

  const assetFormDialogRef = useDialog(onClose);

  return (
    <div ref={assetFormDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">{isEdit ? <Edit2 className="h-6 w-6 text-white" /> : <Plus className="h-6 w-6 text-white" />}</div>
            <div><h2 className="text-xl font-bold text-white">{isEdit ? 'Edit Asset' : 'Add New Asset'}</h2>{isEdit && <p className="text-white/80 text-sm">{asset.assetCode}</p>}</div>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-xl"><X className="h-6 w-6 text-white" /></button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Name - Required */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-semibold mb-2">Asset Name <span className="text-red-500">*</span></label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} onBlur={e => setForm(f => ({...f, name: toTitleCase(e.target.value)}))} className={`w-full p-3 border rounded-xl ${errors.name ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="e.g., ECG Machine" />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Category - Required */}
            <div>
              <label className="block text-sm font-semibold mb-2">Category <span className="text-red-500">*</span></label>
              <Select
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v })}
                options={Object.entries(CATEGORY_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                error={errors.category ? ' ' : undefined}
                buttonClassName="p-3 rounded-xl"
              />
            </div>

            {/* Status - Required */}
            <div>
              <label className="block text-sm font-semibold mb-2">Status <span className="text-red-500">*</span></label>
              <Select
                value={form.status}
                onChange={(v) => setForm({ ...form, status: v })}
                options={Object.entries(STATUS_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                error={errors.status ? ' ' : undefined}
                buttonClassName="p-3 rounded-xl"
              />
            </div>

            {/* Condition - Required */}
            <div>
              <label className="block text-sm font-semibold mb-2">Condition <span className="text-red-500">*</span></label>
              <Select
                value={form.conditionStatus}
                onChange={(v) => setForm({ ...form, conditionStatus: v })}
                options={Object.entries(CONDITION_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))}
                error={errors.conditionStatus ? ' ' : undefined}
                buttonClassName="p-3 rounded-xl"
              />
            </div>

            {/* Optional Fields */}
            <div><label className="block text-sm font-semibold mb-2">Brand</label><input value={form.brand} onChange={e => setForm({...form, brand: e.target.value})} onBlur={e => setForm(f => ({...f, brand: toTitleCase(e.target.value)}))} className="w-full p-3 border border-gray-200 rounded-xl" placeholder="e.g., Philips" /></div>
            <div><label className="block text-sm font-semibold mb-2">Model</label><input value={form.model} onChange={e => setForm({...form, model: e.target.value})} className="w-full p-3 border border-gray-200 rounded-xl" placeholder="e.g., TC-50" /></div>
            <div><label className="block text-sm font-semibold mb-2">Serial Number</label><input value={form.serialNumber} onChange={e => setForm({...form, serialNumber: e.target.value})} className="w-full p-3 border border-gray-200 rounded-xl" /></div>
            <div><label className="block text-sm font-semibold mb-2">Purchase Date</label><DatePicker value={form.purchaseDate} onChange={v => setForm({...form, purchaseDate: v})} max={clinicToday()} placeholder="Select date" /></div>
            <div>
              <label className="block text-sm font-semibold mb-2">Purchase Price (Rs.)</label>
              <input type="number" min="0" value={form.purchasePrice} onChange={e => setForm({...form, purchasePrice: e.target.value})} className={`w-full p-3 border rounded-xl ${errors.purchasePrice ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} />
              {errors.purchasePrice && <p className="text-red-500 text-xs mt-1">{errors.purchasePrice}</p>}
            </div>
            <div><label className="block text-sm font-semibold mb-2">Warranty Expiry</label><DatePicker value={form.warrantyExpiryDate} onChange={v => setForm({...form, warrantyExpiryDate: v})} placeholder="Select date" /></div>
            <div>
              <label className="block text-sm font-semibold mb-2">Vendor</label>
              <Select
                value={vendorId}
                onChange={setVendorId}
                placeholder="Select Vendor"
                options={activeVendors.map(v => ({ value: v.id, label: v.vendorName }))}
                buttonClassName="p-3 rounded-xl"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">Payment Mode</label>
              <Select
                value={form.paymentMode}
                onChange={(v) => setForm({ ...form, paymentMode: v })}
                options={[
                  ...(isEdit && !asset?.paymentMode ? [{ value: '', label: 'Not recorded' }] : []),
                  ...PAYMENT_MODES,
                ]}
                buttonClassName="p-3 rounded-xl"
              />
            </div>
            {paymentNeedsProof(form.paymentMode) && (
              <div className="md:col-span-2">
                <PaymentProofUpload
                  paymentMode={form.paymentMode}
                  files={proofFiles}
                  onFiles={setProofFiles}
                  error={proofError}
                  onClearError={() => setProofError(null)}
                />
              </div>
            )}
            {form.paymentMode && form.paymentMode !== 'CASH' && (
              <div>
                <label className="block text-sm font-semibold mb-2">Instrument / Reference</label>
                <input value={form.paymentReference} onChange={e => setForm({...form, paymentReference: e.target.value})} maxLength={500} className="w-full p-3 border border-gray-200 rounded-xl" placeholder="e.g. Cheque # & bank, transaction ID" />
              </div>
            )}
            <div><label className="block text-sm font-semibold mb-2">Location</label><input value={form.location} onChange={e => setForm({...form, location: e.target.value})} onBlur={e => setForm(f => ({...f, location: toTitleCase(e.target.value)}))} className="w-full p-3 border border-gray-200 rounded-xl" placeholder="e.g., Room 101" /></div>
            <div><label className="block text-sm font-semibold mb-2">Department</label><input value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full p-3 border border-gray-200 rounded-xl" placeholder="e.g., Cardiology" /></div>
            <div><label className="block text-sm font-semibold mb-2">Assigned To</label><input value={form.assignedTo} onChange={e => setForm({...form, assignedTo: e.target.value})} className="w-full p-3 border border-gray-200 rounded-xl" placeholder="e.g., Dr. Ahmed" /></div>
            <div>
              <label className="block text-sm font-semibold mb-2">Maintenance Interval (Days)</label>
              <input type="number" min="0" value={form.maintenanceIntervalDays} onChange={e => setForm({...form, maintenanceIntervalDays: e.target.value})} className={`w-full p-3 border rounded-xl ${errors.maintenanceIntervalDays ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="e.g., 90" />
              {errors.maintenanceIntervalDays && <p className="text-red-500 text-xs mt-1">{errors.maintenanceIntervalDays}</p>}
            </div>
            <div className="lg:col-span-3"><label className="block text-sm font-semibold mb-2">Notes</label><textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full p-3 border border-gray-200 rounded-xl resize-none" placeholder="Additional notes..." /></div>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end gap-3 bg-gray-50">
          <button onClick={onClose} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium">Cancel</button>
          <button onClick={handleSubmit} className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium">{isEdit ? 'Update Asset' : 'Add Asset'}</button>
        </div>
      </div>
    </div>
  );
};

// AssetRow — icon-led label/value row inside the categorized cards. Same shape as the
// VendorRow / UserRow / DetailRow patterns used by the other View modals.
const AssetRow = ({ icon, label, value, mono = false, highlight = false }) => {
  const isEmpty = value == null || value === '';
  return (
    <div className={`flex items-center justify-between gap-4 p-3.5 rounded-xl transition-all duration-200 hover:shadow-sm ${
      highlight ? 'bg-white bg-opacity-80 shadow-sm' : 'bg-white bg-opacity-40'
    }`}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex-shrink-0">{icon}</div>
        <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{label}</span>
      </div>
      <span className={`font-semibold text-sm text-right break-words ${mono ? 'font-mono' : ''} ${
        isEmpty ? 'text-gray-400 italic font-normal' : 'text-gray-900'
      }`}>
        {isEmpty ? 'Not provided' : value}
      </span>
    </div>
  );
};

const ViewModal = ({ asset, onClose }) => {
  const cc = CATEGORY_CONFIG[asset.category] || CATEGORY_CONFIG.OTHER;
  const sc = STATUS_CONFIG[asset.status] || STATUS_CONFIG.ACTIVE;
  const cond = CONDITION_CONFIG[asset.conditionStatus] || CONDITION_CONFIG.GOOD;
  const Icon = cc.icon;
  const formatDate = (d) => formatClinicDate(d, { day: 'numeric', month: 'long', year: 'numeric' }, null);
  const formatPrice = (v) => v ? `Rs. ${parseInt(v).toLocaleString()}` : null;
  // Next maintenance — flag overdue so the row reads at a glance. Pure presentation;
  // the table's amber AlertTriangle uses the same comparison.
  const nextMaintDate = asset.nextMaintenanceDate ? toClinicDate(asset.nextMaintenanceDate) : null;
  const isMaintDue = nextMaintDate && nextMaintDate <= new Date();

  const viewAssetDialogRef = useDialog(onClose, { confirmDiscard: false });

  return (
    <div ref={viewAssetDialogRef} className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">

        {/* Sticky emerald header with dotted pattern — matches Patient / User / Vendor
            View modals. Category icon sits in the avatar tile. */}
        <div className="flex-shrink-0 relative bg-gradient-to-br from-navy-800 via-navy-900 to-navy-950 text-white overflow-hidden">
          <button aria-label="Close" onClick={onClose} className="absolute top-4 right-4 z-10 p-2 hover:bg-white/20 rounded-xl transition-colors">
            <X className="h-5 w-5 text-white" />
          </button>
          <div className="relative p-6">
            <div className="flex items-center space-x-5">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center border-2 border-white border-opacity-30 flex-shrink-0">
                <Icon className="h-8 w-8 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-3xl font-bold text-white">
                  {asset.name}
                </h2>
                <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5 mt-1.5">
                  <span className="text-sm font-mono text-white text-opacity-90">
                    {asset.assetCode}
                  </span>
                  {/* Category chip — uses the asset category's own bg/text tone */}
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-white/40 shadow-sm bg-white text-emerald-700`}>
                    {cc.label}
                  </span>
                  {/* Status chip — warm amber / slate / rose, same palette as the other
                      View modals so status reads consistently across modules. */}
                  {(() => {
                    const tone = {
                      ACTIVE:            'bg-amber-200 border-amber-300 text-amber-900',
                      UNDER_MAINTENANCE: 'bg-sky-200 border-sky-300 text-sky-900',
                      DISPOSED:          'bg-slate-200 border-slate-300 text-slate-800',
                      LOST:              'bg-rose-200 border-rose-300 text-rose-900',
                    }[asset.status] || 'bg-slate-200 border-slate-300 text-slate-800';
                    const dot = {
                      ACTIVE:            'bg-amber-600',
                      UNDER_MAINTENANCE: 'bg-sky-600',
                      DISPOSED:          'bg-slate-500',
                      LOST:              'bg-rose-600',
                    }[asset.status] || 'bg-slate-500';
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border shadow-sm ${tone}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
                        {sc.label}
                      </span>
                    );
                  })()}
                  {/* Condition chip — separate from status so the admin sees both
                      operational state AND physical condition at a glance. */}
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-white/40 shadow-sm ${cond.color}`}>
                    {cond.label}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Maintenance-due banner — surfaces overdue maintenance at the top so the
              admin sees it the moment they open the asset (matches the row's amber
              triangle indicator). */}
          {isMaintDue && asset.status === 'ACTIVE' && (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-amber-800 mb-0.5">Maintenance Due</div>
                  <p className="text-sm text-amber-900">
                    Scheduled maintenance was due on <span className="font-semibold">{formatDate(asset.nextMaintenanceDate)}</span>.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Identification card — brand / model / serial / where it lives */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-11 h-11 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                  <Tag className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Identification
                </h3>
              </div>
              <div className="space-y-3">
                <AssetRow icon={<Briefcase className="h-5 w-5 text-blue-600" />} label="Brand" value={asset.brand} />
                <AssetRow icon={<Box className="h-5 w-5 text-indigo-600" />} label="Model" value={asset.model} />
                <AssetRow icon={<Hash className="h-5 w-5 text-blue-600" />} label="Serial" value={asset.serialNumber} mono />
                <AssetRow icon={<MapPin className="h-5 w-5 text-indigo-600" />} label="Location" value={asset.location} />
                <AssetRow icon={<Building2 className="h-5 w-5 text-blue-600" />} label="Department" value={asset.department} />
                <AssetRow icon={<UserIcon className="h-5 w-5 text-indigo-600" />} label="Assigned To" value={asset.assignedTo} />
              </div>
            </div>

            {/* Purchase & Maintenance card — money + dates */}
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-11 h-11 bg-gradient-to-br from-navy-700 to-navy-900 rounded-xl flex items-center justify-center">
                  <Banknote className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">
                  Purchase & Maintenance
                </h3>
              </div>
              <div className="space-y-3">
                <AssetRow icon={<Banknote className="h-5 w-5 text-emerald-600" />} label="Purchase Price" value={formatPrice(asset.purchasePrice)} mono highlight />
                <AssetRow icon={<Calendar className="h-5 w-5 text-emerald-600" />} label="Purchase Date" value={formatDate(asset.purchaseDate)} />
                <AssetRow icon={<Shield className="h-5 w-5 text-teal-600" />} label="Warranty Until" value={formatDate(asset.warrantyExpiryDate)} />
                <AssetRow icon={<Truck className="h-5 w-5 text-emerald-600" />} label="Vendor" value={asset.vendor?.vendorName} />
                <AssetRow
                  icon={<Banknote className="h-5 w-5 text-teal-600" />}
                  label="Payment"
                  value={asset.paymentMode
                    ? `${PAYMENT_MODE_LABEL[asset.paymentMode] || asset.paymentMode}${asset.paymentReference ? ` · ${asset.paymentReference}` : ''}`
                    : null}
                />
                <AssetRow icon={<Wrench className="h-5 w-5 text-amber-600" />} label="Next Maintenance" value={formatDate(asset.nextMaintenanceDate)} />
              </div>
            </div>
          </div>
        </div>

        {/* Footer — right-aligned emerald Close, consistent with other View modals */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-colors shadow-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const MaintenanceModal = ({ asset, onClose, onSend, onComplete }) => {
  const [notes, setNotes] = useState('');
  const isUnder = asset.status === 'UNDER_MAINTENANCE';
  const maintenanceDialogRef = useDialog(onClose);
  return (
    <div ref={maintenanceDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-6 flex items-center space-x-4"><div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"><Wrench className="h-6 w-6 text-white" /></div><div><h2 className="text-xl font-bold text-white">{isUnder ? 'Complete Maintenance' : 'Send for Maintenance'}</h2><p className="text-white/80 text-sm">{asset.name}</p></div></div>
        <div className="p-6"><p className="text-gray-600 mb-4">{isUnder ? 'Record maintenance completion:' : 'Reason for maintenance:'}</p><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full p-3 border border-gray-200 rounded-xl resize-none" placeholder={isUnder ? "What was done..." : "Why is maintenance needed..."} /></div>
        <div className="p-6 border-t flex justify-end gap-3 bg-gray-50"><button onClick={onClose} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium">Cancel</button><button onClick={() => isUnder ? onComplete(notes) : onSend(notes)} className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl font-medium">{isUnder ? 'Complete' : 'Send'}</button></div>
      </div>
    </div>
  );
};

const DisposeModal = ({ asset, onClose, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }
    onSubmit(reason);
  };

  const disposeDialogRef = useDialog(onClose);

  return (
    <div ref={disposeDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-rose-600 p-6 flex items-center space-x-4"><div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center"><Trash2 className="h-6 w-6 text-white" /></div><div><h2 className="text-xl font-bold text-white">Dispose Asset</h2><p className="text-white/80 text-sm">{asset.name}</p></div></div>
        <div className="p-6">
          <p className="text-gray-600 mb-4">This action cannot be undone. Please provide a reason:</p>
          <textarea value={reason} onChange={e => { setReason(e.target.value); setError(''); }} rows={3} className={`w-full p-3 border rounded-xl resize-none ${error ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="Reason for disposal..." />
          {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        </div>
        <div className="p-6 border-t flex justify-end gap-3 bg-gray-50"><button onClick={onClose} className="px-6 py-3 bg-gray-200 hover:bg-gray-300 rounded-xl font-medium">Cancel</button><button onClick={handleSubmit} className="px-6 py-3 bg-gradient-to-r from-red-500 to-rose-600 text-white rounded-xl font-medium">Dispose</button></div>
      </div>
    </div>
  );
};

export default AssetsManagement;
